import { Employee, DepartmentGroup, Shift, ShiftCategory } from '@/types/shifts';
import { SHIFT_LABELS } from '@/constants/ui';
import { StatIcon } from '@/types/enums';

/**
 * Groups employees by department
 */
export function groupEmployeesByDepartment(employees: Employee[]): DepartmentGroup[] {
  const groups: { [key: string]: Employee[] } = {};
  
  employees.forEach(employee => {
    const dept = employee.department || 'Unassigned';
    if (!groups[dept]) {
      groups[dept] = [];
    }
    groups[dept].push(employee);
  });

  return Object.entries(groups).map(([department, employees]) => ({
    department,
    employees
  }));
}

/**
 * Groups shifts by category (name)
 */
export function groupShiftsByCategory(shifts: Shift[]): ShiftCategory[] {
  const groupedShifts: { [key: string]: Shift[] } = {};
  
  shifts.forEach(shift => {
    const category = shift.name || 'Uncategorized';
    if (!groupedShifts[category]) {
      groupedShifts[category] = [];
    }
    groupedShifts[category].push(shift);
  });

  return Object.entries(groupedShifts).map(([name, shifts]) => ({
    name,
    shifts
  }));
}

/**
 * Sorts departments alphabetically
 */
export function sortDepartments(departments: DepartmentGroup[]): DepartmentGroup[] {
  return [...departments].sort((a, b) => a.department.localeCompare(b.department));
}

/**
 * Sorts employees within departments by name
 */
export function sortEmployeesInDepartments(departments: DepartmentGroup[]): DepartmentGroup[] {
  return departments.map(dept => ({
    ...dept,
    employees: [...dept.employees].sort((a, b) => a.name.localeCompare(b.name))
  }));
}

/**
 * Formats time for display
 */
export function formatTime(time: string | null): string {
  if (!time) return SHIFT_LABELS.SHIFT_DETAILS.NOT_SET;
  return time;
}

/**
 * Formats date for display
 */
export function formatDate(date: string | null): string {
  if (!date) return SHIFT_LABELS.SHIFT_DETAILS.NOT_SET;
  // Parse date string manually to avoid timezone issues
  const [year, month, day] = date.split(/[-T]/);
  if (year && month && day) {
    // Create date in local timezone to prevent day shifts
    const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return dateObj.toLocaleDateString();
  }
  return new Date(date).toLocaleDateString();
}

/**
 * Formats break hours for display
 */
export function formatBreakHours(breakHrs: number | null): string {
  if (breakHrs === null || breakHrs === undefined) return '0 minutes';
  
  const totalMinutes = Math.round(breakHrs * 60);
  
  if (totalMinutes < 60) {
    return `${totalMinutes} minutes`;
  }
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (minutes === 0) {
    return `${hours} ${hours === 1 ? 'hour' : StatIcon.HOURS}`;
  }
  
  return `${hours} ${hours === 1 ? 'hour' : StatIcon.HOURS} and ${minutes} minutes`;
}

/**
 * Gets today's date in YYYY-MM-DD format
 */
export function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Calculates department selection state
 */
export function calculateDepartmentSelectionState(
  department: DepartmentGroup,
  selectedEmployees: Set<string>
): {
  isFullySelected: boolean;
  isPartiallySelected: boolean;
  isSelected: boolean;
  employeeCount: number;
  selectedCount: number;
} {
  const employeeCount = department.employees.length;
  const selectedCount = department.employees.filter(emp => selectedEmployees.has(emp.id)).length;
  const isFullySelected = selectedCount === employeeCount && employeeCount > 0;
  const isPartiallySelected = selectedCount > 0 && selectedCount < employeeCount;
  // Department checkbox should reflect actual employee selection, not the department set membership
  const isSelected = isFullySelected;

  return {
    isFullySelected,
    isPartiallySelected,
    isSelected,
    employeeCount,
    selectedCount
  };
}

// Department tri-state selection
export type DeptSelectionState = 'checked' | 'indeterminate' | 'unchecked';

export function getDeptSelectionState(empIds: string[], selected: Set<string>): DeptSelectionState {
  const total = empIds.length;
  if (total === 0) return 'unchecked';
  const selectedCount = empIds.reduce((count, id) => count + (selected.has(id) ? 1 : 0), 0);
  if (selectedCount === 0) return 'unchecked';
  if (selectedCount === total) return 'checked';
  return 'indeterminate';
}

/**
 * Validates shift selection
 */
export function validateShiftSelection(
  selectedEmployees: Set<string>,
  selectedShift: Shift | null
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (selectedEmployees.size === 0) {
    errors.push(SHIFT_LABELS.SELECT_EMPLOYEE_ERROR);
  }

  if (!selectedShift) {
    errors.push('Please select a shift');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Filters employees by search term
 */
export function filterEmployees(
  employees: Employee[],
  searchTerm: string
): Employee[] {
  if (!searchTerm.trim()) return employees;

  const term = searchTerm.toLowerCase();
  return employees.filter(employee =>
    employee.name.toLowerCase().includes(term) ||
    (employee.department && employee.department.toLowerCase().includes(term))
  );
}

/**
 * Gets unique departments from employees
 */
export function getUniqueDepartments(employees: Employee[]): string[] {
  const departments = new Set(employees.map(emp => emp.department || 'Unassigned'));
  return Array.from(departments).sort();
}

/**
 * Calculates selection statistics
 */
export function calculateSelectionStats(
  departmentGroups: DepartmentGroup[],
  selectedEmployees: Set<string>,
  selectedDepartments: Set<string>
): {
  totalEmployees: number;
  selectedEmployees: number;
  totalDepartments: number;
  selectedDepartments: number;
} {
  const totalEmployees = departmentGroups.reduce((sum, dept) => sum + dept.employees.length, 0);
  const selectedEmployeesCount = selectedEmployees.size;
  const totalDepartments = departmentGroups.length;
  const selectedDepartmentsCount = selectedDepartments.size;

  return {
    totalEmployees,
    selectedEmployees: selectedEmployeesCount,
    totalDepartments,
    selectedDepartments: selectedDepartmentsCount
  };
}

/**
 * Creates a memoized selector for employee selection
 */
export function createEmployeeSelector(employeeId: string) {
  return (selectedEmployees: Set<string>) => selectedEmployees.has(employeeId);
}

/**
 * Creates a memoized selector for department selection
 */
export function createDepartmentSelector(departmentName: string) {
  return (selectedDepartments: Set<string>) => selectedDepartments.has(departmentName);
}

/**
 * Debounces function calls
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number = 100
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Throttles function calls
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number = 100
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
} 